import React, { FC, FormEvent, useState } from 'react';
import { SurveyElement, InputType } from '../../types';
import { useEditorData } from '../../EditorProvider';
import { changeElementData } from '../../utils/surveyDataUtils';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { RemoveButton } from './RemoveButton';
import getAlignment from '../../utils/getAlignment';

interface DropDownInput {
  id: number;
  value: string;
}
interface DropDownProps {
  index: number;
  elementId: string;
  pageId: string;
  elementData: SurveyElement;
}

const DropDown: FC<DropDownProps> = ({
  index,
  elementId,
  pageId,
  elementData,
}) => {
  const [dropDownInputs, setDropDownInputs] = useState<DropDownInput[]>([
    {
      id: 1,
      value: 'Item 1',
    },
    {
      id: 2,
      value: 'Item 2',
    },
    {
      id: 3,
      value: 'Item 3',
    },
  ]);
  const [itemNoneAdded, setItemNoneAdded] = useState(false);
  const [itemOtherAdded, setItemOtherAdded] = useState(false);

  const {
    history,
    setHistory,
    setSelectedItem,
    selectedItem: focus,
    setIsSettingsOpen,
  } = useEditorData();

  const { present: surveyData } = history;

  const onChange = (e: FormEvent<HTMLSpanElement>) => {
    if (!elementData) return;

    const { id, textContent } = e.currentTarget;

    const params = {
      surveyData,
      data: {
        [id]: textContent as string,
        showOtherItem: itemOtherAdded,
        showNoneItem: itemNoneAdded,
      },
      focus,
      setHistory,
    };
    changeElementData(params);
    setIsSettingsOpen(true);
  };

  const addDropDownInput = () => {
    const newInputId = dropDownInputs.length + 1;
    const newDropDownInput: DropDownInput = {
      id: newInputId,
      value: `Item ${newInputId}`,
    };
    setDropDownInputs([...dropDownInputs, newDropDownInput]);
  };

  const removeDropDownInput = (id: number) => {
    const updatedInputs = dropDownInputs.filter((input) => input.id !== id);
    setDropDownInputs(updatedInputs);
  };

  return (
    <div
      className="svc-row"
      onClick={(e) => {
        e.stopPropagation();
        setSelectedItem({ pageId, elementId, type: InputType.DROP_DOWN });
      }}
    >
      <div className="sd-row sd-clearfix sd-page__row">
        <div
          data-key="question10"
          style={{ flex: '1 1 100%', minWidth: '300px', maxWidth: '100%' }}
        >
          <div
            data-sv-drop-target-survey-element="question1"
            className="svc-question__adorner"
          >
            <div
              className="svc-question__content svc-question__content--dropdown svc-question__content--selected"
              tabIndex={0}
              style={{ paddingBottom: 0 }}
            >
              <div className="svc-question__drag-area">
                <svg
                  className="sv-svg-icon svc-question__drag-element"
                  role="img"
                  style={{ width: '24px', height: '24px' }}
                >
                  <use xlinkHref="#icon-drag-area-indicator_24x16" />
                </svg>
              </div>
              <div
                id="sq_4268"
                className="sd-element sd-question sd-row__question sd-row__question--small"
                role="combobox"
                aria-required="false"
                aria-invalid="false"
                aria-labelledby="sq_4268_ariaTitle"
                data-rendered="r"
                data-name="question1"
              >
                <div
                  className={`sd-question__header sd-element__header sd-question__header--location-top sd-element__header--location-top ${getAlignment(
                    surveyData,
                  )}`}
                >
                  <h5
                    className="sd-title sd-element__title sd-question__title sd-element__title--collapsed"
                    id="sq_4268_ariaTitle"
                    aria-label="question1"
                  >
                    <span
                      data-key="q_num"
                      className="sd-element__num"
                      aria-hidden="true"
                      style={{ position: 'static' }}
                    >
                      {index}.
                    </span>
                    <span data-key="num-sp">&nbsp;</span>
                    <span className="svc-string-editor">
                      <span className="svc-string-editor__content">
                        <div className="svc-string-editor__border">
                          <svg
                            className="sv-svg-icon svc-string-editor__button svc-string-editor__button--edit"
                            role="img"
                            style={{ width: '16px', height: '16px' }}
                          >
                            <use xlinkHref="#icon-edit" />
                          </svg>
                        </div>
                        <span className="svc-string-editor__input">
                          <span
                            id="title"
                            role="textbox"
                            className="sv-string-editor"
                            contentEditable="true"
                            spellCheck="false"
                            aria-label="content editable"
                            data-gramm="false"
                            aria-placeholder={`Question ${index}`}
                            onBlur={onChange}
                            suppressContentEditableWarning
                          >
                            {elementData?.title}
                          </span>
                          <div className="svc-string-editor__controls" />
                        </span>
                      </span>
                    </span>
                  </h5>
                  <div className="sd-description sd-question__description">
                    <span
                      className={`svc-string-editor ${
                        !elementData?.description && 'svc-string-editor--hidden'
                      }`}
                    >
                      <span className="svc-string-editor__content">
                        <div className="svc-string-editor__border">
                          <svg
                            className="sv-svg-icon svc-string-editor__button svc-string-editor__button--edit"
                            role="img"
                            style={{ width: '16px', height: '16px' }}
                          >
                            <use xlinkHref="#icon-edit" />
                          </svg>
                        </div>
                        <span className="svc-string-editor__input">
                          <span
                            id="description"
                            role="textbox"
                            className="sv-string-editor"
                            contentEditable="true"
                            spellCheck="false"
                            aria-label="content editable"
                            data-gramm="false"
                            onBlur={onChange}
                            suppressContentEditableWarning
                          />
                          <div className="svc-string-editor__controls" />
                          {elementData.description}
                        </span>
                      </span>
                    </span>
                  </div>
                </div>
                <div className="sd-question__content" role="presentation">
                  <div className="sd-selectbase">
                    <div>
                      <div
                        id="sq_4268i"
                        className="sd-input sd-dropdown sd-dropdown--empty"
                        role="combobox"
                        aria-required="false"
                        aria-label="question1"
                        aria-invalid="false"
                        aria-expanded="false"
                        aria-controls="sq_4268i_list"
                      >
                        <div className="sd-dropdown__value">
                          <input
                            type="text"
                            autoComplete="off"
                            id="sq_4268i_0"
                            className="sd-dropdown__filter-string-input"
                            aria-label="Select..."
                            aria-expanded="false"
                            aria-controls="sq_4268i_list"
                            placeholder="Select..."
                            disabled
                            inputMode="text"
                          />
                        </div>
                        <div
                          className="sd-dropdown_clean-button"
                          style={{ display: 'none' }}
                        >
                          <svg
                            className="sv-svg-icon sd-dropdown_clean-button-svg"
                            role="img"
                            aria-label="Clear"
                          >
                            <use xlinkHref="#icon-clear" />
                            <title>Clear</title>
                          </svg>
                        </div>
                      </div>
                      <div />
                    </div>
                  </div>
                </div>
              </div>
              <div className="svc-question__dropdown-choices--wrapper">
                <div className="svc-question__dropdown-choices">
                  <div>
                    {dropDownInputs.map((input) => (
                      <div
                        className="svc-question__dropdown-choice"
                        key={input.id}
                      >
                        <div
                          className="svc-item-value-wrapper"
                          data-sv-drop-target-item-value="Item 1"
                        >
                          <div className="svc-item-value__ghost" />
                          <div className="svc-item-value-controls">
                            <span className="svc-item-value-controls__button svc-item-value-controls__drag">
                              <svg
                                className="sv-svg-icon svc-item-value-controls__drag-icon"
                                role="img"
                                aria-label="Drag the item"
                                style={{ width: '24px', height: '24px' }}
                              >
                                <use xlinkHref="#icon-drag-area-indicator" />
                                <title>Drag the item</title>
                              </svg>
                            </span>
                            <span
                              className="svc-item-value-controls__button svc-item-value-controls__remove"
                              aria-label="Click to remove the item..."
                              tabIndex={0}
                              onClick={() => removeDropDownInput(input.id)}
                            >
                              <svg
                                className="sv-svg-icon"
                                role="img"
                                aria-label="Click to remove the item..."
                                style={{ width: '16px', height: '16px' }}
                              >
                                <RemoveCircleOutlineIcon color="error" />
                                <title>Click to remove the item...</title>
                              </svg>
                            </span>
                          </div>
                          <div className="svc-item-value__item">
                            <div
                              className="sd-item sd-radio sd-selectbase__item sv-q-col-undefined"
                              role="presentation"
                            >
                              <label
                                className="sd-selectbase__label"
                                aria-label={input.value}
                              >
                                <input
                                  className="sd-visuallyhidden sd-item__control sd-radio__control"
                                  id={`sq_${input.id}`}
                                  type="radio"
                                  name={`question1_sq_${input.id}`}
                                  disabled
                                  defaultValue={input.value}
                                />
                                <span className="sd-item__decorator sd-radio__decorator" />
                                <span className="sd-item__control-label">
                                  <span className="svc-string-editor">
                                    <span className="svc-string-editor__content">
                                      <div className="svc-string-editor__border">
                                        <svg
                                          className="sv-svg-icon svc-string-editor__button svc-string-editor__button--edit"
                                          role="img"
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                          }}
                                        >
                                          <use xlinkHref="#icon-edit" />
                                        </svg>
                                      </div>
                                      <span className="svc-string-editor__input">
                                        <span
                                          id={`item${input.id}`}
                                          role="textbox"
                                          className="sv-string-editor"
                                          contentEditable={true}
                                          spellCheck={false}
                                          aria-placeholder=""
                                          aria-label="content editable"
                                          data-gramm="false"
                                          onBlur={onChange}
                                          suppressContentEditableWarning
                                        >
                                          {input.value}
                                        </span>
                                        <div className="svc-string-editor__controls" />
                                      </span>
                                    </span>
                                  </span>
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="svc-question__dropdown-choice">
                    <div className="svc-item-value-wrapper svc-item-value--new">
                      <div className="svc-item-value__ghost" />
                      <div className="svc-item-value-controls">
                        <span
                          className="svc-item-value-controls__button svc-item-value-controls__add"
                          aria-label="Click to add an item..."
                          tabIndex={0}
                          onClick={addDropDownInput}
                        >
                          <svg
                            className="sv-svg-icon"
                            role="img"
                            aria-label="Click to add an item..."
                            style={{ width: '16px', height: '16px' }}
                          >
                            <AddCircleOutlineIcon color="success" />
                            <title>Click to add an item...</title>
                          </svg>
                        </span>
                      </div>
                      <div className="svc-item-value__item">
                        <div
                          className="sd-item sd-radio sd-selectbase__item sv-q-col-undefined"
                          role="presentation"
                        >
                          <label
                            className="sd-selectbase__label"
                            aria-label="Item 4"
                          >
                            <input
                              className="sd-visuallyhidden sd-item__control sd-radio__control"
                              id="sq_4268i_3"
                              type="radio"
                              name="question1_sq_4268"
                              disabled
                              defaultValue="Item 4"
                            />
                            <span className="sd-item__decorator sd-radio__decorator" />
                            <span className="sd-item__control-label">
                              <span className="sv-string-viewer">
                                {`item ${dropDownInputs.length + 1}`}
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="svc-question__dropdown-choice">
                    <div
                      className={
                        itemNoneAdded === false
                          ? 'svc-item-value-wrapper svc-item-value--new'
                          : 'svc-item-value-wrapper'
                      }
                    >
                      <div className="svc-item-value__ghost" />
                      <div className="svc-item-value-controls">
                        <span
                          className={
                            itemNoneAdded === false
                              ? 'svc-item-value-controls__button svc-item-value-controls__add'
                              : 'svc-item-value-controls__button svc-item-value-controls__remove'
                          }
                          aria-label="Click to add an item..."
                          tabIndex={0}
                          onClick={() => setItemNoneAdded(!itemNoneAdded)}
                        >
                          <svg
                            className="sv-svg-icon"
                            role="img"
                            aria-label="Click to add an item..."
                            style={{ width: '16px', height: '16px' }}
                          >
                            {itemNoneAdded === false ? (
                              <AddCircleOutlineIcon color="success" />
                            ) : (
                              <RemoveCircleOutlineIcon color="error" />
                            )}
                            <title>Click to add an item...</title>
                          </svg>
                        </span>
                      </div>
                      <div className="svc-item-value__item">
                        <div
                          className="sd-item sd-radio sd-selectbase__item sv-q-col-undefined"
                          role="presentation"
                        >
                          <label
                            className="sd-selectbase__label"
                            aria-label="None"
                          >
                            <input
                              className="sd-visuallyhidden sd-item__control sd-radio__control"
                              id="sq_4268i_4"
                              type="radio"
                              name="question1_sq_4268"
                              disabled
                              defaultValue="none"
                            />
                            <span className="sd-item__decorator sd-radio__decorator" />
                            <span className="sd-item__control-label">
                              <span className="svc-string-editor svc-string-editor--readonly">
                                <span className="svc-string-editor__content">
                                  <div className="svc-string-editor__border">
                                    <svg
                                      className="sv-svg-icon svc-string-editor__button svc-string-editor__button--edit"
                                      role="img"
                                      style={{ width: '16px', height: '16px' }}
                                    >
                                      <use xlinkHref="#icon-edit" />
                                    </svg>
                                  </div>
                                  <span className="svc-string-editor__input">
                                    <span
                                      id="noneText"
                                      role="textbox"
                                      className="sv-string-editor"
                                      contentEditable={itemNoneAdded}
                                      spellCheck="false"
                                      aria-label="content editable"
                                      data-gramm="false"
                                      onBlur={onChange}
                                      suppressContentEditableWarning
                                    >
                                      {elementData.noneText || 'None'}
                                    </span>
                                    <div className="svc-string-editor__controls" />
                                  </span>
                                </span>
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="svc-question__dropdown-choice">
                    <div
                      className={
                        itemOtherAdded === false
                          ? 'svc-item-value-wrapper svc-item-value--new'
                          : 'svc-item-value-wrapper'
                      }
                    >
                      <div className="svc-item-value__ghost" />
                      <div className="svc-item-value-controls">
                        <span
                          className={
                            itemOtherAdded === false
                              ? 'svc-item-value-controls__button svc-item-value-controls__add'
                              : 'svc-item-value-controls__button svc-item-value-controls__remove'
                          }
                          aria-label="Click to add an item..."
                          tabIndex={0}
                          onClick={() => setItemOtherAdded(!itemOtherAdded)}
                        >
                          <svg
                            className="sv-svg-icon"
                            role="img"
                            aria-label="Click to add an item..."
                            style={{ width: '16px', height: '16px' }}
                          >
                            {itemOtherAdded === false ? (
                              <AddCircleOutlineIcon color="success" />
                            ) : (
                              <RemoveCircleOutlineIcon color="error" />
                            )}
                            <title>Click to add an item...</title>
                          </svg>
                        </span>
                      </div>
                      <div className="svc-item-value__item">
                        <div
                          className="sd-item sd-radio sd-selectbase__item sv-q-col-undefined"
                          role="presentation"
                        >
                          <label
                            className="sd-selectbase__label"
                            aria-label="Other (describe)"
                          >
                            <input
                              className="sd-visuallyhidden sd-item__control sd-radio__control"
                              id="sq_4268i_5"
                              type="radio"
                              name="question1_sq_4268"
                              disabled
                              defaultValue="other"
                            />
                            <span className="sd-item__decorator sd-radio__decorator" />
                            <span className="sd-item__control-label">
                              <span className="svc-string-editor svc-string-editor--readonly">
                                <span className="svc-string-editor__content">
                                  <div className="svc-string-editor__border">
                                    <svg
                                      className="sv-svg-icon svc-string-editor__button svc-string-editor__button--edit"
                                      role="img"
                                      style={{ width: '16px', height: '16px' }}
                                    >
                                      <use xlinkHref="#icon-edit" />
                                    </svg>
                                  </div>
                                  <span className="svc-string-editor__input">
                                    <span
                                      id="otherText"
                                      role="textbox"
                                      className="sv-string-editor"
                                      contentEditable={itemOtherAdded}
                                      spellCheck="false"
                                      aria-label="content editable"
                                      data-gramm="false"
                                      onBlur={onChange}
                                      suppressContentEditableWarning
                                    >
                                      {elementData.otherText ||
                                        'Other (describe)'}
                                    </span>
                                    <div className="svc-string-editor__controls" />
                                  </span>
                                </span>
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <RemoveButton
                surveyData={surveyData}
                elementId={elementId}
                pageId={pageId}
                setHistory={setHistory}
                setSelectedItem={setSelectedItem}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropDown;
